import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeft,
  ArrowRight,
  Check,
  DollarSign,
  FileText,
  Loader2,
  Pencil,
  Plus,
  Save,
  Send,
  Trash2,
  User
} from 'lucide-react';
import { Contract } from '../../types/contract';
import { contractService } from '../../services/contractService';
import { formatCurrency } from '../../utils/currencyUtils';
import { formatDate } from '../../utils/dateUtils';

const MY_NUMBER_STORAGE_KEY = 'playkids_contract_flow_my_number';

const cleanMoney = (value: string) => value.replace(/[^0-9,]/g, '');
const cleanPhone = (value: string) => value.replace(/\D/g, '');
const isPlainMoneyValue = (value?: string) => {
  const cleaned = (value || '').trim();
  return /^\d+([,.]\d{1,2})?$/.test(cleaned);
};
const withBrazilCode = (value: string) => {
  const digits = cleanPhone(value);
  if (!digits) return '';
  return digits.startsWith('55') ? digits : `55${digits}`;
};

const buildContractReadyMessage = (contract: Contract) => {
  const contractUrl = `https://playkidseventos.com.br/contrato/${contract.id}/pdf`;

  return `Ola ${contract.contratante}! Seu contrato da PlayKids esta pronto.

Evento: ${contract.nomeAniversariante}
Data: ${contract.dataFesta ? formatDate(contract.dataFesta) : 'A definir'}
Horario: ${contract.horarioInicioFesta || 'A definir'}
Valor: ${contract.valor ? formatCurrency(contract.valor) : 'A definir'}

Acesse o contrato no modelo oficial da PlayKids aqui:
${contractUrl}`;
};

const ContractFlowPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [step, setStep] = useState(1);
  const [contract, setContract] = useState<Contract | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [totalValue, setTotalValue] = useState('');
  const [budgetItems, setBudgetItems] = useState<Array<{ label: string; value: string }>>([]);
  const [discountPercentage, setDiscountPercentage] = useState('0');
  const [observacoes, setObservacoes] = useState('');
  const [sendTarget, setSendTarget] = useState<'client' | 'me'>('client');
  const [myNumber, setMyNumber] = useState(() => window.localStorage.getItem(MY_NUMBER_STORAGE_KEY) || '');
  const [editingMyNumber, setEditingMyNumber] = useState(!window.localStorage.getItem(MY_NUMBER_STORAGE_KEY));

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      setLoading(true);
      try {
        const data = await contractService.getContractById(id);
        if (!data) {
          navigate('/admin/contratos');
          return;
        }

        setContract(data);
        setTotalValue(data.valor || '');
        setBudgetItems(data.customBudgetFields || []);
        setDiscountPercentage(data.discountPercentage || '0');
        setObservacoes(data.observacoes || '');
      } catch (error) {
        console.error('Erro ao carregar fluxo do contrato:', error);
        navigate('/admin/contratos');
      } finally {
        setLoading(false);
      }
    };

    load();
  }, [id, navigate]);

  const subtotal = useMemo(() => {
    const itemsTotal = budgetItems.reduce((sum, item) => {
      return sum + (parseFloat(item.value.replace(',', '.')) || 0);
    }, 0);

    const total = budgetItems.length > 0
      ? itemsTotal
      : (parseFloat(totalValue.replace(',', '.')) || 0);

    return total.toFixed(2).replace('.', ',');
  }, [budgetItems, totalValue]);

  const finalAmount = useMemo(() => {
    const subtotalNumber = parseFloat(subtotal.replace(',', '.')) || 0;
    const discount = parseFloat(discountPercentage.replace(',', '.')) || 0;
    if (subtotalNumber <= 0) return '';
    return (subtotalNumber * (1 - discount / 100)).toFixed(2).replace('.', ',');
  }, [subtotal, discountPercentage]);

  const saveBudget = async () => {
    if (!contract || !id) return;
    if (!finalAmount || parseFloat(finalAmount.replace(',', '.')) <= 0) {
      alert('Defina pelo menos um valor antes de gerar o contrato.');
      return;
    }

    setSaving(true);
    try {
      const updates: Partial<Contract> = {
        valor: finalAmount,
        status: 'pendente',
        brinquedosPrice: '',
        recreacaoPrice: '',
        personagensPrice: '',
        personagensList: '',
        customBudgetFields: budgetItems.filter((item) => item.label.trim() || item.value.trim()),
        discountPercentage,
        observacoes,
        restricaoMusical: isPlainMoneyValue(contract.restricaoMusical) ? '' : (contract.restricaoMusical || '')
      };

      await contractService.updateContract(id, updates);
      setContract({ ...contract, ...updates });
      setStep(3);
    } catch (error) {
      console.error('Erro ao salvar fluxo:', error);
      const message = error instanceof Error ? error.message : 'Erro desconhecido.';
      alert(`Erro ao salvar e gerar contrato: ${message}`);
    } finally {
      setSaving(false);
    }
  };

  const sendContract = () => {
    if (!contract) return;

    const targetNumber = sendTarget === 'client' ? contract.telefone : myNumber;
    const phone = withBrazilCode(targetNumber || '');

    if (!phone) {
      alert('Informe um numero valido para envio.');
      return;
    }

    if (sendTarget === 'me') {
      window.localStorage.setItem(MY_NUMBER_STORAGE_KEY, cleanPhone(myNumber));
      setEditingMyNumber(false);
    }

    window.open(`https://wa.me/${phone}?text=${encodeURIComponent(buildContractReadyMessage(contract))}`, '_blank');
  };

  const addBudgetItem = () => {
    setBudgetItems((current) => [...current, { label: '', value: '' }]);
  };

  const updateBudgetItem = (index: number, field: 'label' | 'value', value: string) => {
    setBudgetItems((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index
          ? { ...item, [field]: field === 'value' ? cleanMoney(value) : value }
          : item
      )
    );
  };

  const removeBudgetItem = (index: number) => {
    setBudgetItems((current) => current.filter((_, itemIndex) => itemIndex !== index));
  };

  if (loading) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-teal-600" />
      </div>
    );
  }

  if (!contract) return null;

  return (
    <div className="max-w-5xl mx-auto space-y-5">
      <div className="bg-gradient-to-r from-teal-500 to-teal-600 rounded-lg p-5 text-white">
        <button
          onClick={() => navigate('/admin/contratos')}
          className="mb-4 inline-flex items-center gap-2 rounded-lg bg-white/20 px-3 py-2 text-sm font-semibold hover:bg-white/30"
        >
          <ArrowLeft className="w-4 h-4" />
          Voltar
        </button>
        <h1 className="text-2xl font-bold">Fluxo de contrato</h1>
        <p className="text-teal-50">Defina o preco, salve no contrato e envie o modelo oficial ja usado pelo sistema.</p>
      </div>

      <div className="grid grid-cols-3 gap-2">
        {['Inscricao', 'Preco', 'Enviar'].map((label, index) => {
          const current = index + 1;
          return (
            <div key={label} className={`rounded-lg border p-3 text-sm font-semibold ${step >= current ? 'border-teal-200 bg-teal-50 text-teal-800' : 'border-slate-200 bg-white text-slate-500'}`}>
              {current}. {label}
            </div>
          );
        })}
      </div>

      {step === 1 && (
        <section className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm space-y-4">
          <div className="flex items-center gap-3">
            <User className="w-5 h-5 text-teal-600" />
            <h2 className="text-lg font-semibold text-slate-900">Dados recebidos</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
            <Info label="Cliente" value={contract.contratante} />
            <Info label="Telefone" value={contract.telefone} />
            <Info label="Aniversariante" value={contract.nomeAniversariante} />
            <Info label="Data" value={contract.dataFesta ? formatDate(contract.dataFesta) : 'Nao informada'} />
            <Info label="Horario" value={contract.horarioInicioFesta} />
            <Info label="Endereco" value={`${contract.endereco}, ${contract.numero} - ${contract.cidade}/${contract.estado}`} />
            <Info label="Tema" value={contract.temaFesta || 'Nao informado'} />
            <Info label="Convidados" value={String(contract.numeroConvidados || 'Nao informado')} />
          </div>
          <div className="flex justify-end">
            <button onClick={() => setStep(2)} className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 font-semibold text-white hover:bg-teal-700">
              Definir preco
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </section>
      )}

      {step === 2 && (
        <section className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm space-y-5">
          <div className="flex items-center gap-3">
            <DollarSign className="w-5 h-5 text-teal-600" />
            <h2 className="text-lg font-semibold text-slate-900">Preco e itens</h2>
          </div>
          <div className="rounded-lg border border-slate-200 bg-slate-50 p-4">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <h3 className="font-semibold text-slate-900">Itens livres</h3>
                <p className="text-sm text-slate-500">Adicione itens apenas se quiser detalhar. Se nao quiser, informe somente o valor total abaixo.</p>
              </div>
              <button
                onClick={addBudgetItem}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-white px-3 py-2 text-sm font-semibold text-slate-700 border border-slate-200 hover:bg-slate-100"
              >
                <Plus className="w-4 h-4" />
                Adicionar item
              </button>
            </div>

            {budgetItems.length > 0 && (
              <div className="mt-4 space-y-3">
                {budgetItems.map((item, index) => (
                  <div key={index} className="grid grid-cols-1 md:grid-cols-[1fr_180px_44px] gap-3 items-end">
                    <TextInput
                      label={`Item ${index + 1}`}
                      value={item.label}
                      onChange={(value) => updateBudgetItem(index, 'label', value)}
                      placeholder="Descricao opcional"
                    />
                    <MoneyInput
                      label="Valor"
                      value={item.value}
                      onChange={(value) => updateBudgetItem(index, 'value', value)}
                    />
                    <button
                      onClick={() => removeBudgetItem(index)}
                      className="h-11 inline-flex items-center justify-center rounded-lg bg-red-50 text-red-600 border border-red-100 hover:bg-red-100"
                      title="Remover item"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <MoneyInput
              label={budgetItems.length > 0 ? 'Valor total calculado pelos itens' : 'Valor total do contrato'}
              value={budgetItems.length > 0 ? subtotal : totalValue}
              onChange={setTotalValue}
              disabled={budgetItems.length > 0}
            />
            <MoneyInput label="Desconto (%)" value={discountPercentage} onChange={setDiscountPercentage} />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Observacoes</label>
            <textarea value={observacoes} onChange={(event) => setObservacoes(event.target.value)} rows={4} className="input" />
          </div>
          <div className="rounded-lg bg-teal-50 border border-teal-200 p-4 flex items-center justify-between">
            <span className="font-semibold text-teal-900">Total do contrato</span>
            <strong className="text-2xl text-teal-900">{finalAmount ? formatCurrency(finalAmount) : 'R$ 0,00'}</strong>
          </div>
          <div className="flex justify-between">
            <button onClick={() => setStep(1)} className="rounded-lg border border-slate-200 px-4 py-2 font-semibold text-slate-700 hover:bg-slate-50">Voltar</button>
            <button onClick={saveBudget} disabled={saving} className="inline-flex items-center gap-2 rounded-lg bg-teal-600 px-4 py-2 font-semibold text-white hover:bg-teal-700 disabled:opacity-60">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileText className="w-4 h-4" />}
              Salvar e gerar no modelo do sistema
            </button>
          </div>
        </section>
      )}

      {step === 3 && (
        <section className="bg-white border border-slate-200 rounded-lg p-5 shadow-sm space-y-5">
          <div className="flex items-center gap-3">
            <Check className="w-5 h-5 text-teal-600" />
            <h2 className="text-lg font-semibold text-slate-900">Contrato pronto</h2>
          </div>

          <div className="rounded-lg bg-emerald-50 border border-emerald-200 p-4 text-emerald-800">
            Contrato gerado no modelo oficial do sistema com valor de <strong>{contract.valor ? formatCurrency(contract.valor) : formatCurrency(finalAmount)}</strong>.
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <button onClick={() => setSendTarget('client')} className={`rounded-lg border p-4 text-left ${sendTarget === 'client' ? 'border-teal-500 bg-teal-50' : 'border-slate-200 bg-white'}`}>
              <strong className="block text-slate-900">Enviar para o cliente</strong>
              <span className="text-sm text-slate-500">{contract.telefone}</span>
            </button>
            <button onClick={() => setSendTarget('me')} className={`rounded-lg border p-4 text-left ${sendTarget === 'me' ? 'border-teal-500 bg-teal-50' : 'border-slate-200 bg-white'}`}>
              <strong className="block text-slate-900">Enviar para mim</strong>
              <span className="text-sm text-slate-500">{myNumber || 'Numero ainda nao salvo'}</span>
            </button>
          </div>

          {sendTarget === 'me' && (
            <div className="rounded-lg border border-slate-200 p-4">
              <div className="flex items-center justify-between mb-2">
                <label className="text-sm font-semibold text-slate-700">Meu numero</label>
                {!editingMyNumber && (
                  <button onClick={() => setEditingMyNumber(true)} className="inline-flex items-center gap-1 text-sm font-semibold text-teal-700">
                    <Pencil className="w-4 h-4" />
                    Editar
                  </button>
                )}
              </div>
              <input
                value={myNumber}
                onChange={(event) => setMyNumber(event.target.value.replace(/\D/g, ''))}
                disabled={!editingMyNumber}
                className="input"
                placeholder="Ex: 5533999999999"
              />
            </div>
          )}

          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">Mensagem</label>
            <textarea readOnly value={buildContractReadyMessage(contract)} rows={8} className="input font-mono text-xs" />
          </div>

          <div className="flex flex-col sm:flex-row gap-3">
            <button onClick={() => window.open(`/contrato/${contract.id}/pdf`, '_blank')} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg border border-slate-200 px-4 py-3 font-semibold text-slate-700 hover:bg-slate-50">
              <FileText className="w-4 h-4" />
              Abrir modelo do contrato
            </button>
            <button onClick={sendContract} className="inline-flex flex-1 items-center justify-center gap-2 rounded-lg bg-green-600 px-4 py-3 font-semibold text-white hover:bg-green-700">
              {sendTarget === 'me' && editingMyNumber ? <Save className="w-4 h-4" /> : <Send className="w-4 h-4" />}
              Enviar no WhatsApp
            </button>
          </div>
        </section>
      )}
    </div>
  );
};

const Info = ({ label, value }: { label: string; value?: string }) => (
  <div className="rounded-lg bg-slate-50 border border-slate-200 p-3">
    <span className="block text-slate-500">{label}</span>
    <strong className="text-slate-900">{value || 'Nao informado'}</strong>
  </div>
);

const MoneyInput = ({ label, value, onChange, disabled = false }: { label: string; value: string; onChange: (value: string) => void; disabled?: boolean }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
    <input
      value={value}
      onChange={(event) => onChange(cleanMoney(event.target.value))}
      className="input disabled:opacity-70"
      placeholder="0,00"
      disabled={disabled}
    />
  </div>
);

const TextInput = ({ label, value, onChange, placeholder }: { label: string; value: string; onChange: (value: string) => void; placeholder?: string }) => (
  <div>
    <label className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
    <input value={value} onChange={(event) => onChange(event.target.value)} className="input" placeholder={placeholder} />
  </div>
);

export default ContractFlowPage;
